import { ApiHttpError, fetchWithAuth, getApiBase } from '@/lib/api';
import { clientSurfaceField } from '@/lib/device-tools/client-surface';

export interface PaginationMeta {
  limit: number;
  offset: number;
  count: number;
  total: number;
  hasMore: boolean;
  /** `total` is a lower bound — the server cut its scan short. */
  partial?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  meta?: { version?: string; pagination?: PaginationMeta };
}

export interface ChatSessionDto {
  id: string;
  title: string;
  userId?: string | null;
  user_id?: string | null;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  lastMessageAt?: string | null;
  last_message_at?: string | null;
  isPinned?: boolean;
  is_pinned?: boolean;
  metadata?: Record<string, unknown> | null;
  agentId?: string | null;
  agent_id?: string | null;
  agentName?: string | null;
  agent_name?: string | null;
  agentIsPaused?: boolean;
  agent_is_paused?: boolean;
  agentPauseReason?: string | null;
  agent_pause_reason?: string | null;
  agentCanEdit?: boolean;
  agent_can_edit?: boolean;
  // Owning tenant: opens an owner's chat in the workspace it lives in.
  tenantId?: string | null;
  tenant_id?: string | null;
  // Chat sharing (read-only): set when the current user views a session shared
  // with them (not the owner). ``readOnly`` locks the composer; ``sharedBy``
  // names the owner for the "Geteilt von X" banner.
  readOnly?: boolean;
  read_only?: boolean;
  sharedBy?: string | null;
  shared_by?: string | null;
  // KAN-1540: the reader cannot open this chat's agent (``blockedReason``
  // ``agent_unreachable``, the only reason the API blocks a chat). Locks the
  // composer.
  blocked?: boolean;
  blockedReason?: string | null;
  blocked_reason?: string | null;
  // True when the current (owner) user has actively shared this session
  // (internal or external link). Drives the subtle "shared by you" icon in
  // the sidebar. Only populated by the listing endpoints.
  isShared?: boolean;
  is_shared?: boolean;
  // The project this chat belongs to, and whether it is shared with everyone
  // who can see that project (as opposed to private to its author).
  projectId?: string | null;
  project_id?: string | null;
  projectShared?: boolean;
  project_shared?: boolean;
  // Someone else's chat, reached through a project shared with the caller.
  // Pin, rename and delete are the owner's; ``ownerName`` names them (list
  // rows only).
  ownedByOther?: boolean;
  ownerName?: string | null;
  // The tail of the conversation: where it got to, not what it is about.
  // Only the project chat list carries it.
  preview?: string | null;
  // The caller archived this chat, for themselves alone (KAN-1664).
  isArchived?: boolean;
  is_archived?: boolean;
}

export interface ChatMessageDto {
  id: string;
  session_id: string;
  // The API's ``MessageSender``: an answer is ``ai``, a summary of the history ``system``.
  sender: 'user' | 'ai' | 'system';
  content: string;
  // Author of a user message (shared agent threads have multiple writers).
  // Backend serializes camelCase; tolerate snake_case defensively.
  authorId?: string | null;
  author_id?: string | null;
  authorName?: string | null;
  author_name?: string | null;
  // The caller's own rating of this answer, if any.
  myFeedback?: 'positive' | 'negative' | null;
  model_identifier?: string | null;
  model_id?: string | null;
  modelId?: string | null;
  modelName?: string | null;
  model_name?: string | null;
  servedBy?: string | null;
  served_by?: string | null;
  modelRegion?: string | null;
  model_region?: string | null;
  modelLocation?: string | null;
  model_location?: string | null;
  responseDurationMs?: number | null;
  response_duration_ms?: number | null;
  // Context-window usage derived backend-side from the real LiteLLM `usage`.
  contextUsedTokens?: number | null;
  contextMaxTokens?: number | null;
  contextPercent?: number | null;
  contextSoftLimitTokens?: number | null;
  // Generation-speed metrics (market-standard "output speed"), derived backend-side.
  ttftMs?: number | null;
  tokensPerSecond?: number | null;
  // Duration breakdown for the info popup: tool wall-clock and writing time.
  writingMs?: number | null;
  toolsMs?: number | null;
  thinkingMs?: number | null;
  message_metadata?: Record<string, unknown> | null;
  attachments?: Array<Record<string, unknown>> | null;
  system?: string | null;
  created_at: string;
  updated_at: string;
  parentMessageId?: string | null;
  parent_message_id?: string | null;
  branchIndex?: number;
  branch_index?: number;
  contentBlocks?: Array<Record<string, unknown>> | null;
  content_blocks?: Array<Record<string, unknown>> | null;
  // Where the history was summarised during this answer (KAN-1706).
  compactedAt?: number[];
  // The tool call this answer waits on for the user, while it waits (KAN-1874),
  // with what its dialog is built from (KAN-1875).
  awaitingUser?: {
    toolCallId: string;
    toolName: string;
    dialog?: Record<string, unknown> | null;
  } | null;
}

/** A field under its camelCase name, else its snake_case twin, else ``fallback``. */
const either = <T>(camel: T | undefined, snake: T | undefined, fallback: T): T =>
  camel ?? snake ?? fallback;

/**
 * Every session the API hands out goes through here, list row or single
 * session alike: camelCase names, and a default for every flag left out.
 */
export const normalizeSession = (s: ChatSessionDto): ChatSessionDto => ({
  ...s,
  userId: either(s.userId, s.user_id, null),
  createdAt: either(s.createdAt, s.created_at, undefined),
  updatedAt: either(s.updatedAt, s.updated_at, undefined),
  lastMessageAt: either(s.lastMessageAt, s.last_message_at, null),
  isPinned: either(s.isPinned, s.is_pinned, false),
  metadata: s.metadata ?? {},
  agentId: either(s.agentId, s.agent_id, null),
  agentName: either(s.agentName, s.agent_name, null),
  agentIsPaused: either(s.agentIsPaused, s.agent_is_paused, false),
  agentPauseReason: either(s.agentPauseReason, s.agent_pause_reason, null),
  agentCanEdit: either(s.agentCanEdit, s.agent_can_edit, false),
  tenantId: either(s.tenantId, s.tenant_id, null),
  readOnly: either(s.readOnly, s.read_only, false),
  sharedBy: either(s.sharedBy, s.shared_by, null),
  blocked: s.blocked ?? false,
  blockedReason: either(s.blockedReason, s.blocked_reason, null),
  isShared: either(s.isShared, s.is_shared, false),
  projectId: either(s.projectId, s.project_id, null),
  projectShared: either(s.projectShared, s.project_shared, false),
  ownedByOther: s.ownedByOther ?? false,
  ownerName: s.ownerName ?? null,
  preview: s.preview ?? null,
  isArchived: either(s.isArchived, s.is_archived, false),
});

export async function listSessions(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  agentId?: string;
}): Promise<{ sessions: ChatSessionDto[]; pagination?: PaginationMeta }> {
  const limit = params?.limit ?? 25;
  const offset = params?.offset ?? 0;
  const searchParams = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (params?.search?.trim()) {
    searchParams.set('search', params.search.trim());
  }
  if (params?.agentId) {
    searchParams.set('agentId', params.agentId);
  }
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions?${searchParams.toString()}`
  );
  const body = (await res.json()) as ApiResponse<ChatSessionDto[]>;
  const sessions = (body.data ?? []).map(normalizeSession);
  return { sessions, pagination: body.meta?.pagination };
}

export async function createSession(
  title?: string,
  agentId?: string,
  guidedPreset?: string,
  guidedContext?: Record<string, unknown>,
  projectId?: string,
  projectShared?: boolean,
  /**
   * Greeting that opens an agent thread in place of the agent's own start
   * message ("Änderung besprechen"). Only with ``agentId``, and the backend
   * only accepts it from someone who may edit that agent.
   */
  startMessage?: string
): Promise<ChatSessionDto> {
  const payload: Record<string, unknown> = {};
  if (title) payload.title = title;
  if (agentId) payload.agentId = agentId;
  if (agentId && startMessage) payload.startMessage = startMessage;
  if (projectId) payload.projectId = projectId;
  if (projectId && projectShared) payload.projectShared = true;
  if (guidedPreset) payload.guidedPreset = guidedPreset;
  if (guidedContext) payload.guidedContext = guidedContext;
  // A chat started on a device surface, the Outlook pane, is one of that
  // surface for good: the server gives it the surface's tools (KAN-1785).
  Object.assign(payload, clientSurfaceField());

  const res = await fetchWithAuth(`${getApiBase()}/api/v1/chat/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (await res.json()) as ApiResponse<ChatSessionDto>;
  return normalizeSession(body.data);
}

/** What a guided chat opens with, before it exists. */
export interface GuidedPresetPreview {
  startMessage: string;
  modelId: string | null;
}

/**
 * The greeting and model a guided chat would be created with, without
 * creating it. Refuses exactly where creating would (licence, permission,
 * feature, access to the target), so an entry point learns it on the click.
 */
export async function previewGuidedPreset(
  guidedPreset: string,
  guidedContext?: Record<string, unknown>
): Promise<GuidedPresetPreview> {
  const payload: Record<string, unknown> = { guidedPreset };
  if (guidedContext) payload.guidedContext = guidedContext;
  const res = await fetchWithAuth(`${getApiBase()}/api/v1/chat/guided-presets/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (await res.json()) as ApiResponse<GuidedPresetPreview>;
  return { startMessage: body.data.startMessage, modelId: body.data.modelId ?? null };
}

export async function getSession(sessionId: string): Promise<ChatSessionDto> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`
  );
  const body = (await res.json()) as ApiResponse<ChatSessionDto>;
  return normalizeSession(body.data);
}

/**
 * The workspace a chat lives in, for a chat the caller owns or holds a share
 * for (KAN-1754). Opening such a chat from another of their workspaces fails
 * there; this names the workspace to move to. Throws (404) for every other
 * chat, so it tells nobody anything about chats they could not open anyway.
 */
export async function locateChat(sessionId: string): Promise<string> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/locate`
  );
  const body = (await res.json()) as ApiResponse<{ tenantId: string }>;
  return body.data.tenantId;
}

export async function updateSession(
  sessionId: string,
  payload: { title?: string; metadata?: Record<string, unknown> }
): Promise<ChatSessionDto> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const body = (await res.json()) as ApiResponse<ChatSessionDto>;
  return normalizeSession(body.data);
}

export async function pinSession(sessionId: string, pinned: boolean): Promise<ChatSessionDto> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/pin`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned }),
    }
  );
  const body = (await res.json()) as ApiResponse<ChatSessionDto>;
  return normalizeSession(body.data);
}

/**
 * Archive a chat for the caller, or bring it back (KAN-1664). Only their own
 * lists change; everyone else who sees the chat keeps it.
 */
export async function archiveSession(
  sessionId: string,
  archived: boolean
): Promise<ChatSessionDto> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/archive`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived }),
    }
  );
  const body = (await res.json()) as ApiResponse<ChatSessionDto>;
  return normalizeSession(body.data);
}

export async function listMessages(
  sessionId: string,
  params?: { limit?: number; offset?: number }
): Promise<ChatMessageDto[]> {
  const limit = params?.limit ?? 25;
  const offset = params?.offset ?? 0;
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages?limit=${limit}&offset=${offset}`
  );
  const body = (await res.json()) as ApiResponse<ChatMessageDto[]>;
  return body.data;
}

export interface SubagentCostItem {
  sessionId: string;
  title: string;
  cost: number;
}

/**
 * Aggregated display cost of a session INCLUDING its hidden subagent
 * transcripts. `totalCost` supersedes the client-side sum over visible
 * messages, which misses subagent spend entirely.
 */
export interface SessionCostResponse {
  ownCost: number;
  subagentCost: number;
  totalCost: number;
  subagents: SubagentCostItem[];
}

export async function getSessionCost(sessionId: string): Promise<SessionCostResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/cost`
  );
  const body = (await res.json()) as ApiResponse<SessionCostResponse>;
  return body.data;
}

/** One tool call a sub-agent made, as its chat entry lists it. */
export interface SubAgentToolStep {
  id: string;
  name: string;
  args: Record<string, unknown>;
}

/**
 * What the sub-agent a tool call of a chat started did, and how it ended
 * (KAN-1616). `outcome` is null when the run stored no answer. Its reasoning is
 * not in it: no answer stores reasoning, so it is only there while a run is live.
 */
export interface SubAgentRun {
  steps: SubAgentToolStep[];
  outcome: 'done' | 'stopped' | 'failed' | null;
  /** The call followed up on a sub-agent an earlier call started (KAN-1617). */
  resumed: boolean;
  /** The run is still at work (KAN-1872). */
  running: boolean;
  /** It runs, or ran, in the background: the chat's AI did not wait for it (KAN-1872). */
  background: boolean;
  /** When a run still at work started (ISO), for its running time after a reload; else none. */
  startedAt?: string | null;
  /** What the run came back with (markdown), once it is over; none for a stopped one. */
  result?: string | null;
  /** The sources the run cited, as an answer's sources are listed. */
  sources?: SubAgentSource[];
}

/** One source a sub-agent cited. */
export interface SubAgentSource {
  title: string | null;
  url: string | null;
}

/** The sources as listed, each with a title or an address to show. */
const sourcesOf = (value: unknown): SubAgentSource[] =>
  (Array.isArray(value) ? value : [])
    .map((item: Record<string, unknown> | null) => ({
      title: typeof item?.title === 'string' && item.title.trim() ? item.title : null,
      url: typeof item?.url === 'string' && item.url.trim() ? item.url : null,
    }))
    .filter((source) => source.title || source.url);

const subAgentRunUrl = (sessionId: string, callId: string, action = '') =>
  `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/subagent-runs/${encodeURIComponent(callId)}${action}`;

/**
 * The run a tool call of the chat started. A call that started none (refused
 * before it ran, or from before calls were recorded) answers 404 and reads as
 * a run without steps. `running` and `background` read as false where an API
 * from before KAN-1872 leaves them out, `result` as none and `sources` as none.
 */
export async function getSubAgentRun(sessionId: string, callId: string): Promise<SubAgentRun> {
  let res: Response;
  try {
    res = await fetchWithAuth(subAgentRunUrl(sessionId, callId));
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 404) {
      return { steps: [], outcome: null, resumed: false, running: false, background: false };
    }
    throw error;
  }
  const { data } = (await res.json()) as ApiResponse<SubAgentRun>;
  return {
    ...data,
    running: data.running === true,
    background: data.background === true,
    startedAt: typeof data.startedAt === 'string' ? data.startedAt : null,
    result: typeof data.result === 'string' && data.result.trim() ? data.result : null,
    sources: sourcesOf(data.sources),
  };
}

/**
 * Sends a running sub-agent the chat's AI waits for into the background: the
 * AI goes on answering, the sub-agent goes on working (KAN-1872). Refused
 * with `SUBAGENT_NOT_DETACHABLE` when it is over or in the background already.
 */
export async function detachSubAgentRun(sessionId: string, callId: string): Promise<void> {
  await fetchWithAuth(subAgentRunUrl(sessionId, callId, '/detach'), { method: 'POST' });
}

/**
 * Stops a sub-agent running in the background (KAN-1872). Refused with
 * `SUBAGENT_NOT_RUNNING` when it is over already.
 */
export async function stopSubAgentRun(sessionId: string, callId: string): Promise<void> {
  await fetchWithAuth(subAgentRunUrl(sessionId, callId, '/stop'), { method: 'POST' });
}

/**
 * The summary request behind the context gauge's button (KAN-1576): a mark on
 * the chat that its next turn takes, summarising the history before it answers.
 */
async function compactionRequest(
  sessionId: string,
  method: 'GET' | 'PUT' | 'DELETE'
): Promise<boolean> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/compaction`,
    { method }
  );
  if (!res.ok) throw new Error(`Compaction request failed: ${res.status}`);
  const body = (await res.json()) as ApiResponse<{ requested: boolean }>;
  return body.data.requested;
}

export const getCompactionRequest = (sessionId: string) => compactionRequest(sessionId, 'GET');
export const requestCompaction = (sessionId: string) => compactionRequest(sessionId, 'PUT');
export const cancelCompaction = (sessionId: string) => compactionRequest(sessionId, 'DELETE');

export interface ActiveTaskResponse {
  has_active_task: boolean;
  task_state: 'running' | 'completed' | 'error' | null;
  /** UUID of the user message owning the running run (only when running). */
  user_message_id?: string | null;
  /**
   * The answer the running run writes on: set while it carries on an answer
   * that waited for the user's answer in a tool call (KAN-1874).
   */
  continuesMessageId?: string | null;
}

export async function getActiveTask(sessionId: string): Promise<ActiveTaskResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/active-task`
  );
  const body = (await res.json()) as ApiResponse<ActiveTaskResponse>;
  return body.data;
}

export async function cancelTask(sessionId: string): Promise<void> {
  await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/cancel`,
    { method: 'POST' }
  );
}

// ── Chat sharing ───────────────────────────────────────────────────────────

export interface ShareGrantee {
  userId: string;
  name: string;
  /** Per-person write right: true = "Bearbeiten", false = "Lesen" (default). */
  allowWrite?: boolean;
  /** Only on an agent chat: false when the person needs Business to see it (KAN-1434). */
  canOpenAgentChats?: boolean | null;
}

export interface ShareLinkResponse {
  active: boolean;
  token?: string | null;
  urlPath?: string | null;
  accessCount?: number;
  /**
   * Display names of everyone who currently has access (owner-facing,
   * alphabetical). Legacy — prefer `grants`, which carries the user id too.
   */
  grantees?: string[];
  /**
   * Everyone who currently has access, with id + name (owner-facing,
   * alphabetical by name). Populated independently of whether an open link
   * exists — direct-share access persists without a link. The dialog uses
   * this to render each person with a per-person "remove" action.
   */
  grants?: ShareGrantee[];
  /** Collaborative sharing: grant holders may write, not only read. */
  allowWrite?: boolean;
}

/**
 * Create (or reactivate) the share link for a session. Owner only.
 * Re-POSTing with a different `allowWrite` toggles collaboration on the
 * existing link without changing its URL.
 */
export async function createShareLink(
  sessionId: string,
  options?: { allowWrite?: boolean }
): Promise<ShareLinkResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ allowWrite: options?.allowWrite ?? false }),
    }
  );
  const body = (await res.json()) as ApiResponse<ShareLinkResponse>;
  return body.data;
}

/** Current share-link state for a session. Owner only. */
export async function getShareLink(sessionId: string): Promise<ShareLinkResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share`
  );
  const body = (await res.json()) as ApiResponse<ShareLinkResponse>;
  return body.data;
}

/** Disable the link and drop every read grant. Owner only. */
export async function revokeShareLink(sessionId: string): Promise<void> {
  await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share`,
    { method: 'DELETE' }
  );
}

/**
 * Grant access to a specific same-tenant user (direct sharing). Owner only.
 * Independent of the link. ``allowWrite`` selects the per-person right: omit or
 * `false` shares read-only ("Lesen"), `true` grants "Bearbeiten". Re-calling
 * for an existing person with a different value flips their right without
 * re-adding them. Returns the refreshed share state.
 */
export async function addShareGrant(
  sessionId: string,
  userId: string,
  options?: { allowWrite?: boolean }
): Promise<ShareLinkResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share/grants`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, allowWrite: options?.allowWrite ?? false }),
    }
  );
  const body = (await res.json()) as ApiResponse<ShareLinkResponse>;
  return body.data;
}

/** Remove one user's read grant on a session. Owner only. Returns the refreshed state. */
export async function removeShareGrant(
  sessionId: string,
  userId: string
): Promise<ShareLinkResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share/grants/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  const body = (await res.json()) as ApiResponse<ShareLinkResponse>;
  return body.data;
}

export interface ExternalShareResponse {
  active: boolean;
  /** Relative external link path incl. token; prefix with the app origin. */
  urlPath?: string | null;
  /** When the external link stops working (ISO string), null when inactive. */
  expiresAt?: string | null;
}

/**
 * Enable (or re-issue) the external read-only, PIN-gated link. Owner only.
 * Feature-flag gated (`external_chat_sharing`). Re-POSTing rotates the token
 * and resets the PIN + 30-day expiry.
 */
export async function enableExternalShare(
  sessionId: string,
  pin: string
): Promise<ExternalShareResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share/external`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    }
  );
  const body = (await res.json()) as ApiResponse<ExternalShareResponse>;
  return body.data;
}

/** Current external-share state for a session. Owner only. */
export async function getExternalShare(sessionId: string): Promise<ExternalShareResponse> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share/external`
  );
  const body = (await res.json()) as ApiResponse<ExternalShareResponse>;
  return body.data;
}

/** Disable the external link (clears token, PIN, expiry). Owner only. */
export async function disableExternalShare(sessionId: string): Promise<void> {
  await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share/external`,
    { method: 'DELETE' }
  );
}

/** Redeem a share link → durable read grant for the current user. */
export async function redeemShareLink(sessionId: string, token: string): Promise<ChatSessionDto> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/share/redeem`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }
  );
  const body = (await res.json()) as ApiResponse<ChatSessionDto>;
  return normalizeSession(body.data);
}

/** Sessions shared read-only with the current user ("Geteilt mit mir"). */
/**
 * Which area a shared-chat list is asked for: one agent, one project, or —
 * with neither — the plain chat sidebar (KAN-1416).
 */
export interface SharedChatsQuery {
  agentId?: string;
  projectId?: string;
}

async function listSharedIn(path: string, query: SharedChatsQuery): Promise<ChatSessionDto[]> {
  const params = new URLSearchParams();
  if (query.agentId) params.set('agentId', query.agentId);
  if (query.projectId) params.set('projectId', query.projectId);
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : '';
  const res = await fetchWithAuth(`${getApiBase()}/api/v1/chat/sessions/${path}${suffix}`);
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  const body = (await res.json()) as ApiResponse<ChatSessionDto[]>;
  return (body.data ?? []).map(normalizeSession);
}

/** Chats shared with the current user ("Geteilt mit mir") in one area. */
export function listSharedSessions(query: SharedChatsQuery): Promise<ChatSessionDto[]> {
  return listSharedIn('shared', query);
}

/** The current user's own chats that they have actively shared ("Von mir geteilt") in one area. */
export function listSharedByMeSessions(query: SharedChatsQuery): Promise<ChatSessionDto[]> {
  return listSharedIn('shared-by-me', query);
}

/** What the current user archived in one area ("Archiviert", KAN-1664), whole. */
export function listArchivedSessions(query: SharedChatsQuery): Promise<ChatSessionDto[]> {
  return listSharedIn('archived', query);
}

export async function deleteSession(sessionId: string): Promise<void> {
  const res = await fetchWithAuth(
    `${getApiBase()}/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'DELETE',
      redirectOn401: true,
    }
  );
  if (!res.ok && res.status !== 204) throw new Error(`deleteSession failed: ${res.status}`);
}
